/**
 * The two kinds of date PayRight shows, and the only ways it shows them.
 *
 * A calendar date — a notice's due date, the day a letter was mailed, a
 * recording date — is a day on the calendar. It reads the same in every time
 * zone: October 11 is October 11 in Honolulu and in Tokyo. The API sends these
 * as "YYYY-MM-DD", or as a timestamp at noon UTC on that day; either way only
 * the date part counts, and it is never converted to the viewer's zone.
 *
 * A moment — when something was confirmed, uploaded or sent — happened at an
 * instant, and reads in the viewer's own time.
 *
 * `new Date("2026-10-11")` is midnight UTC, which is October 10 anywhere west of
 * London; shown with toLocaleDateString it moves a deadline a day earlier. Never
 * show a calendar date that way: use calendarDate.
 */
const civilParts = (value: string) => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/) ?? value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  return value.includes('/') ? { year: +match[3]!, month: +match[1]!, day: +match[2]! } : { year: +match[1]!, month: +match[2]!, day: +match[3]! };
};

const calendarStyles = {
  medium: { dateStyle: 'medium' }, long: { dateStyle: 'long' },
  /** "Sunday, October 11, 2026": a due date, whose weekday says whether the post office is open. */
  full: { dateStyle: 'full' },
  /** "Sun, Oct 11, 2026", for a table. */
  weekday: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
} satisfies Record<string, Intl.DateTimeFormatOptions>;

/** A calendar date as written, for any viewer: "Oct 11, 2026". Accepts YYYY-MM-DD (with or without a time after it) or MM/DD/YYYY. */
export function calendarDate(value: string, style: keyof typeof calendarStyles = 'medium'): string {
  const parts = civilParts(value);
  if (!parts) return value;
  return new Date(parts.year, parts.month - 1, parts.day, 12).toLocaleDateString([], calendarStyles[style]);
}

/** The day a moment fell on, where the viewer is. */
export const momentDate = (value: string) => new Date(value).toLocaleDateString([], { dateStyle: 'medium' });
/** A moment, where the viewer is: "Oct 1, 2026, 9:30 PM". */
export const momentDateTime = (value: string) => new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

/** The time of day of a moment, where the viewer is. */
export const momentTime = (value: string) => new Date(value).toLocaleTimeString();

/** Today's calendar date (YYYY-MM-DD) on this device; `toISOString()` would give UTC's, already tomorrow in a US evening. */
export function localToday(now = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Today where a job is, from the zone the API gives it. Days to a notice's due
 * date count from here: at 10 p.m. in California it is already tomorrow at a
 * Florida job, and that is the day the deadline is measured on.
 */
export function todayIn(zone: string, now = new Date()): string {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(now).map(part => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** A zone's everyday name: "Eastern Time". */
export const zoneName = (zone: string, now = new Date()) =>
  new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longGeneric' }).formatToParts(now).find(part => part.type === 'timeZoneName')?.value ?? zone;

/** Whether the viewer keeps a different time from a zone, so a count of days needs saying whose they are. */
export const viewerIsElsewhere = (zone: string, now = new Date()) =>
  zoneName(zone, now) !== zoneName(Intl.DateTimeFormat().resolvedOptions().timeZone, now);

/** "Due in 3 days", "Due today", "2 days late": calendar days from today (where the job is) to a due date. */
export function dueLabel(dueDate: string | null, today: string) {
  if (!dueDate) return 'No due date';
  const days = daysBetween(today, dueDate);
  return days === 0 ? 'Due today' : days === 1 ? 'Due tomorrow' : days > 1 ? `Due in ${days} days` : `${-days} ${days === -1 ? 'day' : 'days'} late`;
}

/** "9 days left", "tomorrow", "today", "2 days late". */
export function daysLeft(dueDate: string, today: string) {
  const days = daysBetween(today, dueDate);
  return days > 1 ? `${days} days left` : days === 1 ? 'tomorrow' : days === 0 ? 'today' : `${-days} ${days === -1 ? 'day' : 'days'} late`;
}

/** Whole calendar days from one calendar date to another, the same in every zone. */
export function daysBetween(from: string, to: string): number {
  const day = (value: string) => { const parts = civilParts(value)!; return Date.UTC(parts.year, parts.month - 1, parts.day); };
  return Math.round((day(to) - day(from)) / 86_400_000);
}
